<template>
  <div class="home-page">
    <!-- Header -->
    <a-layout-header class="header">
      <div class="header-content">
        <div class="logo-container" @click="$router.push('/')">
          <span class="logo-icon">X</span>
          <div class="logo-text">xxxxproxy</div>
        </div>
        <a-menu
          theme="light"
          mode="horizontal"
          :default-selected-keys="['1']"
          class="nav-menu"
        >
          <a-menu-item key="1"><router-link to="/">{{ $t('menu.home') }}</router-link></a-menu-item>
          <a-menu-item key="2"><router-link to="/dynamic-residential-proxy">{{ $t('menu.dynamicProxy') }}</router-link></a-menu-item>
          <a-menu-item key="static"><router-link to="/static-residential-proxy">{{ $t('menu.staticProxy') }}</router-link></a-menu-item>
          <a-menu-item key="3"><router-link to="/help-center">{{ $t('menu.helpCenter') }}</router-link></a-menu-item>
        </a-menu>
        <div class="auth-buttons">
          <a-dropdown :trigger="['hover']" placement="bottomCenter">
            <a-button type="link" class="lang-btn">
              <a-icon type="global" /> {{ $i18n.locale === 'zh-CN' ? '中文' : 'EN' }} <a-icon type="down" style="font-size: 10px; margin-left: 2px;" />
            </a-button>
            <a-menu slot="overlay" @click="handleLangClick">
              <a-menu-item key="zh-CN">🇨🇳 中文</a-menu-item>
              <a-menu-item key="en-US">🇺🇸 English</a-menu-item>
            </a-menu>
          </a-dropdown>

          <template v-if="!isLoggedIn">
            <a-button type="link" class="login-btn" @click="$router.push('/login')">{{ $t('menu.login') }}</a-button>
            <a-button type="primary" class="register-btn" @click="$router.push('/register')">{{ $t('menu.register') }}</a-button>
          </template>
          <template v-else>
            <div class="user-profile-wrapper">
              <a-dropdown :trigger="['hover']" placement="bottomRight" :getPopupContainer="triggerNode => triggerNode.parentNode">
                <div class="user-profile-dropdown" @click="$router.push('/dashboard/auth')">
                  <a-avatar icon="user" class="avatar" />
                  <span class="username">{{ username }}</span>
                  <a-icon type="down" />
                </div>
                <a-menu slot="overlay" class="user-dropdown-menu">
                  <a-menu-item key="dashboard" @click="$router.push('/dashboard/auth')">
                    <a-icon type="dashboard" /> {{ $t('menu.dashboard') }}
                  </a-menu-item>
                  <a-menu-divider />
                  <a-menu-item key="logout" @click="handleLogout" class="logout-item">
                    <a-icon type="logout" /> {{ $t('menu.logout') }}
                  </a-menu-item>
                </a-menu>
              </a-dropdown>
            </div>
          </template>
          <a-icon type="menu" class="mobile-menu-btn" @click="showMobileMenu = true" />
        </div>
      </div>
    </a-layout-header>

    <!-- Mobile Menu Drawer -->
    <a-drawer
      placement="right"
      :visible="showMobileMenu"
      @close="showMobileMenu = false"
      :closable="false"
      width="280"
      wrapClassName="mobile-menu-drawer"
    >
      <div class="drawer-content">
        <div class="drawer-header">
          <div class="drawer-brand">
            <span class="logo-icon">S</span>
            <span class="logo-text">xxxxproxy</span>
          </div>
          <div class="close-btn" @click="showMobileMenu = false">
            <a-icon type="close" />
          </div>
        </div>
        
        <div class="drawer-menu">
          <router-link to="/" class="menu-link" exact-active-class="active">
            <a-icon type="home" /> {{ $t('menu.home') }}
          </router-link>
          <router-link to="/dynamic-residential-proxy" class="menu-link" active-class="active">
            <a-icon type="thunderbolt" /> {{ $t('menu.dynamicProxy') }}
          </router-link>
          <router-link to="/static-residential-proxy" class="menu-link" active-class="active">
            <a-icon type="cloud-server" /> {{ $t('menu.staticProxy') }}
          </router-link>
          <router-link to="/help-center" class="menu-link" active-class="active">
            <a-icon type="question-circle" /> {{ $t('menu.helpCenter') }}
          </router-link>
          <div class="menu-link" @click="toggleMobileLang" style="cursor: pointer;">
            <a-icon type="global" /> {{ $i18n.locale === 'zh-CN' ? 'Switch to English' : '切换到中文' }}
          </div>
        </div>

        <div class="drawer-auth">
          <template v-if="!isLoggedIn">
            <a-button block size="large" class="drawer-login-btn" @click="$router.push('/login')">{{ $t('menu.login') }}</a-button>
            <a-button block type="primary" size="large" class="drawer-register-btn" @click="$router.push('/register')">{{ $t('register.registerBtn') }}</a-button>
          </template>
          <template v-else>
            <div class="drawer-user-info">
              <a-avatar size="large" icon="user" class="avatar" />
              <div class="user-details">
                <span class="username">{{ username }}</span>
                <span class="status">在线</span>
              </div>
            </div>
            <a-button block size="large" class="drawer-dashboard-btn" @click="$router.push('/dashboard/auth')">
              <a-icon type="dashboard" /> {{ $t('menu.dashboard') }}
            </a-button>
            <a-button block size="large" type="danger" ghost class="drawer-logout-btn" @click="handleLogout">
              <a-icon type="logout" /> {{ $t('menu.logout') }}
            </a-button>
          </template>
        </div>
      </div>
    </a-drawer>

    <!-- Hero Section -->
    <div class="hero-section">
      <div class="hero-bg-animated">
        <div class="glow-orb glow-1"></div>
        <div class="glow-orb glow-2"></div>
        <div class="glow-orb glow-3"></div>
      </div>
      <div class="hero-content">
        <h1 class="hero-title">
          <span class="gradient-text">{{ $t('home.hero.title') }}</span>
        </h1>
        <p class="hero-subtitle" v-html="$t('home.hero.subtitle')"></p>
        <div class="hero-actions">
          <a-button type="primary" size="large" class="cta-btn" @click="$router.push('/login')">{{ $t('home.hero.buyNow') }} <a-icon type="arrow-right" /></a-button>
          <a-button size="large" ghost class="contact-btn" @click="handleContactClick">{{ $t('home.hero.contactSales') }}</a-button>
        </div>
        
        <!-- Banner Features -->
        <div class="banner-features-grid">
          <div class="feature-glass-card" v-for="(item, index) in bannerFeatures" :key="index">
            <div class="glass-icon-wrapper">
              <a-icon :type="item.icon" />
            </div>
            <div class="glass-content">
              <h4>{{ item.title }}</h4>
              <p>{{ item.desc }}</p>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Statistics Section -->
    <div class="stats-section">
      <div class="stats-container">
        <div class="stat-item" v-for="(stat, key) in statsData" :key="key">
          <div class="stat-value">
            <span class="counter">{{ formatNumber(countUps[key]) }}</span>
            <span class="suffix">{{ stat.suffix }}</span>
          </div>
          <div class="stat-label">{{ stat.label }}</div>
        </div>
      </div>
    </div>

    <!-- Core Features -->
    <div class="features-section">
      <div class="section-header">
        <span class="section-tag">{{ $t('home.features.title') }}</span>
        <h2 class="section-title">{{ $t('home.features.subtitle') }}</h2>
      </div>
      <a-row :gutter="[32, 32]" class="features-grid">
        <a-col :md="6" :sm="12" :xs="24" v-for="(feature, index) in features" :key="index">
          <div class="feature-box">
            <div class="feature-icon-box">
              <a-icon :type="feature.icon" />
            </div>
            <h3>{{ feature.title }}</h3>
            <p>{{ feature.description }}</p>
            <div class="hover-bg"></div>
          </div>
        </a-col>
      </a-row>
    </div>

    <!-- Product Introduction -->
    <div class="product-section">
      <div class="product-container">
        <a-row :gutter="{ md: 64, sm: 24, xs: 24 }" type="flex" align="middle">
          <a-col :md="12" :sm="24" :xs="24">
            <div class="product-info">
              <span class="product-badge">{{ $t('home.product.badge') }}</span>
              <h2>{{ $t('home.product.title') }}</h2>
              <p class="product-desc">
                {{ $t('home.product.desc') }}
              </p>
              <div class="product-specs">
                <div class="spec-item">
                  <div class="spec-value">9000万+</div>
                  <div class="spec-label">{{ $t('home.product.pool') }}</div>
                </div>
                <div class="spec-item">
                  <div class="spec-value">99.2%</div>
                  <div class="spec-label">{{ $t('home.product.success') }}</div>
                </div>
                <div class="spec-item">
                  <div class="spec-value">195+</div>
                  <div class="spec-label">{{ $t('home.product.coverage') }}</div>
                </div>
              </div>
              <a-button type="primary" size="large" class="learn-more-btn" @click="$router.push('/dynamic-residential-proxy')">
                {{ $t('home.product.learnMore') }}
              </a-button>
            </div>
          </a-col>
          <a-col :md="12" :sm="24" :xs="24">
            <div class="product-visual">
              <div class="visual-card main-visual">
                <img src="https://images.unsplash.com/photo-1451187580459-43490279c0fa?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80" alt="Proxy Visualization" />
                <div class="visual-overlay"></div>
              </div>
              <div class="visual-card floating-card card-1">
                <a-icon type="global" />
                <span>{{ $t('home.banner.global') }}</span>
              </div>
              <div class="visual-card floating-card card-2">
                <a-icon type="safety" />
                <span>{{ $t('home.banner.safe') }}</span>
              </div>
            </div>
          </a-col>
        </a-row>
      </div>
    </div>

    <!-- Use Cases -->
    <div class="use-cases-section">
      <div class="section-header center">
        <h2 class="section-title">{{ $t('home.useCases.title') }}</h2>
        <p class="section-desc">{{ $t('home.useCases.desc') }}</p>
      </div>
      <div class="use-cases-grid">
        <div class="use-case-card" v-for="(useCase, index) in useCases" :key="index">
          <div class="card-inner">
            <div class="case-icon">
              <a-icon :type="useCase.icon" />
            </div>
            <h4>{{ useCase.name }}</h4>
            <div class="case-arrow">
              <a-icon type="arrow-right" />
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Global Map Section -->
    <div class="global-map-section">
      <div class="map-bg"></div>
      <div class="map-content">
        <h2 class="section-title text-white">{{ $t('home.map.title') }}</h2>
        <p class="section-desc text-white-50">{{ $t('home.map.desc') }}</p>
        
        <div class="country-list-wrapper">
          <div class="country-pill" v-for="(country, index) in countries" :key="index">
            <img :src="`https://flagcdn.com/w40/${country.code}.png`" :alt="country.name" class="flag-icon" />
            <div class="country-info">
              <span class="name">{{ country.name }}</span>
              <span class="count">{{ country.count }} IP</span>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Connect Section (Floating) -->
    <div class="connect-section">
      <div class="connect-container">
        <a href="mailto:zongtaiyida@gmail.com" class="connect-item">
          <a-icon type="message" />
          <span class="tooltip">{{ $t('home.footer.contact') }}</span>
        </a>
        <a href="mailto:zongtaiyida@gmail.com" class="connect-item">
          <a-icon type="mail" />
          <span class="tooltip">{{ $t('home.footer.contact') }}</span>
        </a>
        <!-- <div class="connect-item wechat-item">
          <a-icon type="wechat" />
          <span class="tooltip">客户经理</span>
          <div class="qrcode-popup">
            <img src="https://api.qrserver.com/v1/create-qr-code/?size=120x120&data=mailto:zongtaiyida@gmail.com" alt="QR Code" />
          </div>
        </div> -->
      </div>
    </div>

    <!-- Footer -->
    <a-layout-footer class="footer">
      <div class="footer-content">
        <a-row :gutter="48">
          <a-col :md="8" :sm="24" :xs="24">
            <div class="footer-brand">
              <div class="footer-logo">xxxxproxy</div>
              <p>{{ $t('home.footer.desc') }}</p>
              <!-- <div class="social-links">
                <a href="#"><a-icon type="twitter" /></a>
                <a href="#"><a-icon type="facebook" /></a>
                <a href="#"><a-icon type="linkedin" /></a>
                <a href="#"><a-icon type="github" /></a>
              </div> -->
            </div>
          </a-col>
          <a-col :md="16" :sm="24" :xs="24">
            <a-row :gutter="24">
              <a-col :span="8">
                <h4>{{ $t('home.footer.products') }}</h4>
                <ul>
                  <li><router-link to="/dynamic-residential-proxy">{{ $t('menu.dynamicProxy') }}</router-link></li>
                  <li><router-link to="/static-residential-proxy">{{ $t('menu.staticProxy') }}</router-link></li>
                  <!-- <li><router-link to="#">静态住宅代理</router-link></li>
                  <li><router-link to="#">数据中心代理</router-link></li> -->
                </ul>
              </a-col>
              <a-col :span="8">
                <h4>{{ $t('home.footer.support') }}</h4>
                <ul>
                  <li><router-link to="/help-center">{{ $t('menu.helpCenter') }}</router-link></li>
                  <!-- <li><router-link to="#">API文档</router-link></li>
                  <li><router-link to="#">系统状态</router-link></li> -->
                </ul>
              </a-col>
              <a-col :span="8">
                <h4>{{ $t('home.footer.contact') }}</h4>
                <ul>
                  <li>Email: zongtaiyida@gmail.com</li>
                  <!-- <li>Tel: +1 (555) 123-4567</li>
                  <li>Add: 123 Proxy Street, Cyber City</li> -->
                </ul>
              </a-col>
            </a-row>
          </a-col>
        </a-row>
        <div class="copyright">
          {{ $t('home.footer.copyright', { year: new Date().getFullYear() }) }}
        </div>
      </div>
    </a-layout-footer>

    <!-- Back Top -->
    <a-back-top :visibilityHeight="300">
      <div class="back-to-top-btn">
        <a-icon type="arrow-up" />
      </div>
    </a-back-top>
  </div>
</template>

<script>
export default {
  name: 'Home_view',
  data() {
    return {
      isLoggedIn: false,
      username: '',
      showMobileMenu: false,
      countUps: {
        ipPool: 0,
        dailyUpdate: 0,
        successRate: 0,
        locations: 0
      }
    };
  },
  computed: {
    statsData() {
      return {
        ipPool: { label: this.$t('home.stats.ipPool'), suffix: '+' },
        dailyUpdate: { label: this.$t('home.stats.dailyUpdate'), suffix: '+' },
        successRate: { label: this.$t('home.stats.successRate'), suffix: '%' },
        locations: { label: this.$t('home.stats.locations'), suffix: '+' }
      };
    },
    bannerFeatures() {
      return [
        { title: this.$t('home.banner.global'), desc: this.$t('home.banner.globalDesc'), icon: 'global' },
        { title: this.$t('home.banner.safe'), desc: this.$t('home.banner.safeDesc'), icon: 'safety-certificate' },
        { title: this.$t('home.banner.exclusive'), desc: this.$t('home.banner.exclusiveDesc'), icon: 'database' },
        { title: this.$t('home.banner.support'), desc: this.$t('home.banner.supportDesc'), icon: 'customer-service' }
      ];
    },
    features() {
      return [
        {
          title: this.$t('home.features.concurrency'),
          description: this.$t('home.features.concurrencyDesc'),
          icon: 'thunderbolt'
        },
        {
          title: this.$t('home.features.resources'),
          description: this.$t('home.features.resourcesDesc'),
          icon: 'global'
        },
        {
          title: this.$t('home.features.diversity'),
          description: this.$t('home.features.diversityDesc'),
          icon: 'appstore'
        },
        {
          title: this.$t('home.features.service'),
          description: this.$t('home.features.serviceDesc'),
          icon: 'customer-service'
        }
      ];
    },
    useCases() {
      return [
        { name: this.$t('home.useCases.dataCollection'), icon: 'database' },
        { name: this.$t('home.useCases.brandProtection'), icon: 'safety-certificate' },
        { name: this.$t('home.useCases.marketing'), icon: 'shop' },
        { name: this.$t('home.useCases.seo'), icon: 'search' },
        { name: this.$t('home.useCases.priceComparison'), icon: 'bar-chart' },
        { name: this.$t('home.useCases.security'), icon: 'lock' },
        { name: this.$t('home.useCases.reviewMonitoring'), icon: 'message' },
        { name: this.$t('home.useCases.adVerification'), icon: 'file-protect' }
      ];
    },
    countries() {
      return [
        { name: this.$t('countries.us'), count: '11M+', code: 'us' },
        { name: this.$t('countries.br'), count: '9.9M+', code: 'br' },
        { name: this.$t('countries.de'), count: '5.9M+', code: 'de' },
        { name: this.$t('countries.fr'), count: '8.2M+', code: 'fr' },
        { name: this.$t('countries.in'), count: '4.3M+', code: 'in' },
        { name: this.$t('countries.ca'), count: '14M+', code: 'ca' },
        { name: this.$t('countries.gb'), count: '7.2M+', code: 'gb' },
        { name: this.$t('countries.es'), count: '16M+', code: 'es' },
      ];
    }
  },
  mounted() {
    this.checkLoginStatus();
    this.animateNumbers();
    this.initScrollReveal();
  },
  methods: {
    checkLoginStatus() {
      const token = localStorage.getItem('Token');
      const username = localStorage.getItem('userName');
      if (token && username) {
        this.isLoggedIn = true;
        this.username = username;
      }
    },
    handleLangClick({ key }) {
      this.$i18n.locale = key;
      localStorage.setItem('language', key);
    },
    toggleMobileLang() {
      const newLang = this.$i18n.locale === 'zh-CN' ? 'en-US' : 'zh-CN';
      this.$i18n.locale = newLang;
      localStorage.setItem('language', newLang);
    },
    handleContactClick() {
      window.location.href = 'mailto:zongtaiyida@gmail.com';
    },
    handleLogout() {
      localStorage.clear();
      this.isLoggedIn = false;
      this.username = '';
      this.$message.success('退出登录成功');
      this.$router.push('/login');
    },
    formatNumber(num) {
      if (typeof num === 'number') {
        return num.toLocaleString();
      }
      return num;
    },
    animateNumbers() {
      const targets = {
        ipPool: 53121245,
        dailyUpdate: 3634881,
        successRate: 99.2,
        locations: 195
      };
      
      const duration = 2000;
      const frameDuration = 1000 / 60;
      const totalFrames = Math.round(duration / frameDuration);
      
      let frame = 0;
      const timer = setInterval(() => {
        frame++;
        const progress = frame / totalFrames;
        const easeOutQuad = t => t * (2 - t);
        
        if (frame <= totalFrames) {
          this.countUps.ipPool = Math.round(targets.ipPool * easeOutQuad(progress));
          this.countUps.dailyUpdate = Math.round(targets.dailyUpdate * easeOutQuad(progress));
          this.countUps.successRate = Number((targets.successRate * easeOutQuad(progress)).toFixed(1));
          this.countUps.locations = Math.round(targets.locations * easeOutQuad(progress));
        } else {
          this.countUps = targets;
          clearInterval(timer);
        }
      }, frameDuration);
    },
    initScrollReveal() {
      const observer = new IntersectionObserver((entries) => {
        entries.forEach(entry => {
          if (entry.isIntersecting) {
            entry.target.classList.add('is-visible');
            observer.unobserve(entry.target);
          }
        });
      }, { threshold: 0.1 });

      setTimeout(() => {
        const elements = document.querySelectorAll('.features-section, .product-section, .use-cases-section, .global-map-section');
        elements.forEach(el => {
          el.classList.add('scroll-reveal');
          observer.observe(el);
        });
      }, 100);
    }
  }
};
</script>

<style lang="less">
.user-dropdown-menu {
  padding: 8px;
  border-radius: 12px;
  box-shadow: 0 10px 30px -10px rgba(0, 0, 0, 0.1), 0 4px 12px rgba(0, 0, 0, 0.05);
  border: 1px solid #f0f7ff;
  background: rgba(255, 255, 255, 0.95);
  backdrop-filter: blur(12px);
  min-width: 160px;
  
  .ant-dropdown-menu-item {
    border-radius: 8px;
    padding: 10px 16px;
    margin-bottom: 4px;
    font-weight: 500;
    color: #475569;
    transition: all 0.2s;
    display: flex;
    align-items: center;
    
    .anticon {
      margin-right: 12px;
      font-size: 16px;
      color: #94a3b8;
      transition: all 0.2s;
    }
    
    &:last-child {
      margin-bottom: 0;
    }
    
    &:hover {
      background: #eff6ff;
      color: #1890ff;
      transform: translateX(2px);
      
      .anticon {
        color: #1890ff;
      }
    }
  }

  .ant-dropdown-menu-item-divider {
    background-color: #f1f5f9;
    margin: 6px 0;
  }
  
  .logout-item {
    &:hover {
      background: #fff1f2;
      color: #ef4444;
      
      .anticon {
        color: #ef4444;
      }
    }
  }
}
</style>

<style scoped lang="less">
// 变量定义
@primary-color: #1890ff;
@secondary-color: #69c0ff;
@dark-bg: #0A192F;
@card-bg: #ffffff;
@text-main: #1e293b;
@text-light: #64748b;

.home-page {
  font-family: 'Inter', -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif;
  overflow-x: hidden;
  background-color: #f8fafc;
}

// 动画类
.scroll-reveal {
  opacity: 0;
  transform: translateY(40px);
  transition: all 0.8s cubic-bezier(0.22, 1, 0.36, 1);
}
.scroll-reveal.is-visible {
  opacity: 1;
  transform: translateY(0);
}

// Header
.header {
  background: rgba(255, 255, 255, 0.9);
  backdrop-filter: blur(10px);
  box-shadow: 0 1px 0 rgba(0,0,0,0.05);
  position: fixed;
  width: 100%;
  z-index: 1000;
  height: 72px;
  line-height: 72px;
  padding: 0;
  transition: all 0.3s ease;
  
  &.scrolled {
    box-shadow: 0 4px 20px rgba(0,0,0,0.05);
  }
}

.header-content {
  max-width: 1280px;
  margin: 0 auto;
  padding: 0 24px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  height: 100%;
}

.logo-container {
  display: flex;
  align-items: center;
  gap: 12px;
  cursor: pointer;
  
  .logo-icon {
    width: 36px;
    height: 36px;
    background: linear-gradient(135deg, @primary-color, @secondary-color);
    border-radius: 10px;
    color: #fff;
    display: flex;
    align-items: center;
    justify-content: center;
    font-weight: 800;
    font-size: 20px;
    box-shadow: 0 4px 12px rgba(61, 90, 254, 0.2);
    }
  
    .logo-text {
    font-size: 22px;
    font-weight: 800;
    color: @text-main;
    letter-spacing: -0.5px;
  }
}

.nav-menu {
  border: none;
  background: transparent;
  line-height: 70px;
  
  .ant-menu-item {
    font-size: 15px;
    font-weight: 500;
    margin: 0 16px;
    top: 0;
    
    a {
      color: @text-main;
      &:hover {
        color: @primary-color;
      }
    }
    
    &-selected {
      border-bottom: 2px solid @primary-color;
      a { color: @primary-color; }
    }
  }
}

.auth-buttons {
    display: flex;
    align-items: center;
    gap: 12px;
    
    .lang-btn {
      color: @text-main;
      font-weight: 500;
      padding: 0 8px;
      display: flex;
      align-items: center;
      gap: 4px;
      
      &:hover {
        color: @primary-color;
      }
    }
    
    .login-btn {
    color: @text-main;
    font-weight: 600;
    &:hover { color: @primary-color; }
  }
  
  .register-btn {
    height: 40px;
    padding: 0 24px;
    border-radius: 20px;
    font-weight: 600;
    background: @text-main;
    border-color: @text-main;
    box-shadow: 0 4px 12px rgba(0,0,0,0.1);
    
    &:hover {
      background: @primary-color;
      border-color: @primary-color;
      transform: translateY(-1px);
    }
  }

  .user-profile-wrapper {
    padding-bottom: 10px;
    margin-bottom: -10px;
  }
  
  .user-profile-dropdown {
    display: flex;
    align-items: center;
    gap: 10px;
    padding: 4px 8px 4px 4px;
    height: 40px;
    background: transparent;
    border: 1px solid transparent;
    border-radius: 20px;
    cursor: pointer;
    transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
    line-height: 1.5;
    
    &:hover {
      background: rgba(0, 0, 0, 0.04);
      
      .username {
        color: @primary-color;
      }
      
      .anticon-down {
        color: @primary-color;
        transform: rotate(180deg);
      }
    }
    
    .avatar {
      width: 30px;
      height: 30px;
      line-height: 30px;
      font-size: 16px;
      background: linear-gradient(135deg, @primary-color, @secondary-color);
      border: 1px solid #fff;
      box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
      transition: all 0.3s;
    }
    
    .username {
      font-weight: 600;
      color: @text-main;
      font-size: 14px;
      max-width: 100px;
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
      transition: color 0.3s;
    }
    
    .anticon-down {
      font-size: 12px;
      color: #94a3b8;
      margin-right: 4px;
      transition: all 0.3s;
    }
  }
}

.mobile-menu-btn {
  display: none;
  font-size: 24px;
  cursor: pointer;
}

// Hero Section
.hero-section {
  position: relative;
  padding: 180px 0 120px;
  background: @dark-bg;
  color: #fff;
  overflow: hidden;
  text-align: center;
}

.hero-bg-animated {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  overflow: hidden;
  
  .glow-orb {
    position: absolute;
    border-radius: 50%;
    filter: blur(80px);
    opacity: 0.4;
    animation: float 10s infinite ease-in-out;
  }
  
  .glow-1 {
    width: 400px;
    height: 400px;
    background: @primary-color;
    top: -100px;
    left: -100px;
  }
  
  .glow-2 {
    width: 500px;
    height: 500px;
    background: @secondary-color;
    bottom: -100px;
    right: -100px;
    animation-delay: -2s;
  }
  
  .glow-3 {
    width: 300px;
    height: 300px;
    background: #06b6d4;
    top: 40%;
    left: 50%;
    transform: translate(-50%, -50%);
    opacity: 0.2;
    animation-delay: -5s;
  }
}

@keyframes float {
  0%, 100% { transform: translate(0, 0); }
  50% { transform: translate(30px, 30px); }
}

.hero-content {
  position: relative;
  z-index: 10;
  max-width: 1000px;
  margin: 0 auto;
  padding: 0 24px;
  
  .hero-title {
    font-size: 64px;
    font-weight: 800;
    line-height: 1.1;
    margin-bottom: 24px;
    color: #fff;
    
    .gradient-text {
      background: linear-gradient(135deg, #fff 0%, #64B5F6 100%);
      -webkit-background-clip: text;
      -webkit-text-fill-color: transparent;
    }
  }
  
  .hero-subtitle {
    font-size: 20px;
    color: #94a3b8;
    line-height: 1.6;
    margin-bottom: 48px;
    max-width: 700px;
    margin-left: auto;
    margin-right: auto;
  }
  
  .hero-actions {
    display: flex;
    justify-content: center;
    gap: 20px;
    margin-bottom: 80px;
    
    .cta-btn {
      height: 56px;
      padding: 0 40px;
      font-size: 18px;
      border-radius: 28px;
      background: linear-gradient(135deg, @primary-color, @secondary-color);
      border: none;
      box-shadow: 0 0 0 4px rgba(61, 90, 254, 0.2);
      
      &:hover {
        transform: translateY(-2px);
        box-shadow: 0 10px 25px rgba(61, 90, 254, 0.4);
      }
    }
    
    .contact-btn {
      height: 56px;
      padding: 0 40px;
      font-size: 18px;
      border-radius: 28px;
      border: 1px solid rgba(255,255,255,0.2);
      color: #fff;
      
      &:hover {
        background: rgba(255,255,255,0.1);
        border-color: #fff;
      }
    }
  }
}

.banner-features-grid {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 24px;
  
  .feature-glass-card {
    background: rgba(255, 255, 255, 0.05);
    backdrop-filter: blur(12px);
    border: 1px solid rgba(255, 255, 255, 0.1);
    border-radius: 16px;
    padding: 24px;
    text-align: left;
    transition: all 0.3s;
    
    &:hover {
      background: rgba(255, 255, 255, 0.1);
      transform: translateY(-5px);
    }
    
    .glass-icon-wrapper {
      width: 48px;
      height: 48px;
      background: rgba(61, 90, 254, 0.2);
      border-radius: 12px;
      display: flex;
      align-items: center;
      justify-content: center;
      margin-bottom: 16px;
      color: #64B5F6;
      font-size: 24px;
    }
    
    h4 {
      color: #fff;
      font-size: 16px;
      font-weight: 700;
      margin-bottom: 4px;
    }
    
    p {
      color: #94a3b8;
      font-size: 13px;
      margin: 0;
    }
  }
}

// Stats Section
.stats-section {
  background: #fff;
  padding: 40px 0;
  border-bottom: 1px solid #f1f5f9;
}

.stats-container {
  max-width: 1280px;
  margin: 0 auto;
  display: flex;
  justify-content: space-around;
  flex-wrap: wrap;
  padding: 0 24px;
  
  .stat-item {
    text-align: center;
    padding: 20px;
    
    .stat-value {
      font-size: 40px;
      font-weight: 800;
      color: @text-main;
      line-height: 1;
      margin-bottom: 8px;
      background: linear-gradient(135deg, @text-main 0%, @primary-color 100%);
      -webkit-background-clip: text;
      -webkit-text-fill-color: transparent;
      
      .suffix {
        font-size: 24px;
        color: @primary-color;
        -webkit-text-fill-color: @primary-color;
      }
    }
    
    .stat-label {
      color: @text-light;
      font-weight: 500;
      font-size: 14px;
      text-transform: uppercase;
      letter-spacing: 1px;
    }
  }
}

// Features Section
.features-section {
  padding: 100px 0;
  max-width: 1280px;
  margin: 0 auto;
  padding-left: 24px;
  padding-right: 24px;
}

.section-header {
  text-align: center;
  margin-bottom: 60px;
  
  .section-tag {
    display: inline-block;
    padding: 6px 16px;
    background: #e0e7ff;
    color: @primary-color;
    border-radius: 20px;
    font-size: 13px;
    font-weight: 600;
    margin-bottom: 16px;
  }
  
  .section-title {
    font-size: 36px;
    font-weight: 800;
    color: @text-main;
    margin: 0;
  }
}

.feature-box {
  background: #fff;
  border-radius: 20px;
  padding: 40px 30px;
  height: 100%;
  position: relative;
  overflow: hidden;
  transition: all 0.4s ease;
  border: 1px solid #f1f5f9;
  
  &:hover {
    transform: translateY(-10px);
    box-shadow: 0 20px 40px rgba(0,0,0,0.08);
    border-color: transparent;
    
    .feature-icon-box {
      background: @primary-color;
      color: #fff;
      transform: scale(1.1) rotate(5deg);
    }
  }
  
  .feature-icon-box {
    width: 64px;
    height: 64px;
    background: #f1f5f9;
    border-radius: 16px;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 28px;
    color: @text-main;
    margin-bottom: 24px;
    transition: all 0.4s ease;
  }
  
  h3 {
    font-size: 20px;
    font-weight: 700;
    color: @text-main;
    margin-bottom: 12px;
  }
  
  p {
    color: @text-light;
    line-height: 1.6;
    margin: 0;
  }
}

// Product Section
.product-section {
  background: #fff;
  padding: 100px 0;
}

.product-container {
  max-width: 1280px;
  margin: 0 auto;
  padding: 0 24px;
}

.product-info {
  padding-right: 40px;
  
  .product-badge {
    color: #ef4444;
    font-weight: 700;
    font-size: 14px;
    letter-spacing: 1px;
    text-transform: uppercase;
    display: block;
    margin-bottom: 12px;
  }
  
  h2 {
    font-size: 42px;
    font-weight: 800;
    color: @text-main;
    margin-bottom: 24px;
    line-height: 1.1;
  }
  
  .product-desc {
    font-size: 18px;
    color: @text-light;
    line-height: 1.7;
    margin-bottom: 40px;
  }
  
  .product-specs {
    display: flex;
    gap: 40px;
    margin-bottom: 40px;
    
    .spec-value {
      font-size: 28px;
      font-weight: 800;
      color: @text-main;
    }
    
    .spec-label {
      font-size: 14px;
      color: @text-light;
    }
  }
  
  .learn-more-btn {
    height: 52px;
    padding: 0 32px;
    border-radius: 12px;
    font-size: 16px;
    font-weight: 600;
    background: @text-main;
    border: none;
    
    &:hover {
      background: @primary-color;
      transform: translateY(-2px);
    }
  }
}

.product-visual {
  position: relative;
  
  .visual-card {
    background: #fff;
    border-radius: 24px;
    box-shadow: 0 20px 50px rgba(0,0,0,0.1);
    overflow: hidden;
    
    &.main-visual {
      height: 400px;
      img {
        width: 100%;
        height: 100%;
        object-fit: cover;
      }
    }
    
    &.floating-card {
      position: absolute;
      padding: 16px 24px;
      display: flex;
      align-items: center;
      gap: 12px;
      font-weight: 600;
      color: @text-main;
      animation: float 6s infinite ease-in-out;
      z-index: 2;
      
      i {
        font-size: 20px;
        color: @primary-color;
      }
      
      &.card-1 {
        top: 40px;
        left: -40px;
      }
      
      &.card-2 {
        bottom: 40px;
        right: -20px;
        animation-delay: -3s;
      }
    }
  }
}

// Use Cases
.use-cases-section {
  padding: 100px 0;
  max-width: 1280px;
  margin: 0 auto;
  padding-left: 24px;
  padding-right: 24px;
}

.section-desc {
  color: @text-light;
  font-size: 18px;
  text-align: center;
  max-width: 600px;
  margin: 0 auto 60px;
}

.use-cases-grid {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 24px;
  
  .use-case-card {
    background: #fff;
    border-radius: 16px;
    padding: 30px;
    cursor: pointer;
    transition: all 0.3s;
    border: 1px solid #f1f5f9;
    
    &:hover {
      border-color: @primary-color;
      transform: translateY(-5px);
      box-shadow: 0 10px 30px rgba(99, 102, 241, 0.1);
      
      .case-icon {
        background: @primary-color;
        color: #fff;
      }
      
      .case-arrow {
        opacity: 1;
        transform: translateX(0);
      }
    }
    
    .card-inner {
      display: flex;
      flex-direction: column;
      align-items: center;
      text-align: center;
    }
    
    .case-icon {
      width: 56px;
      height: 56px;
      background: #f8fafc;
      border-radius: 50%;
      display: flex;
      align-items: center;
      justify-content: center;
      font-size: 24px;
      color: @text-main;
      margin-bottom: 16px;
      transition: all 0.3s;
    }
    
    h4 {
      font-size: 16px;
      font-weight: 600;
      color: @text-main;
      margin: 0;
    }
    
    .case-arrow {
      margin-top: 16px;
      color: @primary-color;
      opacity: 0;
      transform: translateX(-10px);
      transition: all 0.3s;
    }
  }
}

// Global Map
.global-map-section {
  background: @dark-bg;
  padding: 100px 0;
  position: relative;
  overflow: hidden;
  text-align: center;
}

.map-bg {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-image: url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23ffffff' fill-opacity='0.05'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E");
}

.map-content {
  position: relative;
  z-index: 10;
  max-width: 1280px;
  margin: 0 auto;
  padding: 0 24px;
}

.text-white { color: #fff !important; }
.text-white-50 { color: rgba(255,255,255,0.6) !important; }

.country-list-wrapper {
  display: flex;
  justify-content: center;
  flex-wrap: wrap;
  gap: 16px;
  margin-top: 60px;
  
  .country-pill {
    background: rgba(255,255,255,0.1);
    backdrop-filter: blur(10px);
    padding: 8px 16px;
    border-radius: 40px;
    display: flex;
    align-items: center;
    gap: 12px;
    border: 1px solid rgba(255,255,255,0.1);
    transition: all 0.3s;
    cursor: default;
    
    &:hover {
      background: rgba(255,255,255,0.2);
      transform: translateY(-3px);
    }
    
    .flag-icon {
      width: 24px;
      height: 24px;
      border-radius: 50%;
      object-fit: cover;
    }
    
    .country-info {
      text-align: left;
      line-height: 1.2;
      
      .name {
        display: block;
        font-size: 14px;
        font-weight: 600;
        color: #fff;
      }
      
      .count {
        display: block;
        font-size: 12px;
        color: rgba(255,255,255,0.7);
      }
    }
  }
}

// Connect Section
.connect-section {
  position: fixed;
  right: 24px;
  bottom: 40%;
  z-index: 99;
  
  .connect-container {
    display: flex;
    flex-direction: column;
    gap: 12px;
  }
  
  .connect-item {
    width: 56px;
    height: 56px;
    background: #fff;
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 24px;
    color: @text-main;
    box-shadow: 0 4px 12px rgba(0,0,0,0.1);
    transition: all 0.3s;
    position: relative;
    cursor: pointer;
    
    &:hover {
      background: @primary-color;
      color: #fff;
      transform: translateY(-3px);
      
      .tooltip {
        opacity: 1;
        visibility: visible;
        transform: translateX(0);
      }
      
      .qrcode-popup {
        opacity: 1;
        visibility: visible;
        transform: translateX(0);
      }
    }
    
    .tooltip {
      position: absolute;
      right: 70px;
      background: @text-main;
      color: #fff;
      padding: 6px 12px;
      border-radius: 6px;
      font-size: 13px;
      white-space: nowrap;
      opacity: 0;
      visibility: hidden;
      transform: translateX(10px);
      transition: all 0.3s;
      pointer-events: none;
      
      &::after {
        content: '';
        position: absolute;
        right: -4px;
        top: 50%;
        transform: translateY(-50%);
        border-width: 4px;
        border-style: solid;
        border-color: transparent transparent transparent @text-main;
      }
    }
    
    .qrcode-popup {
      position: absolute;
      right: 70px;
      bottom: 0;
      background: #fff;
      padding: 10px;
      border-radius: 12px;
      box-shadow: 0 10px 30px rgba(0,0,0,0.15);
      opacity: 0;
      visibility: hidden;
      transform: translateX(10px);
      transition: all 0.3s;
      
      img {
        width: 120px;
        height: 120px;
        display: block;
      }
    }
  }
}

// Footer
.footer {
  background: #fff;
  padding: 80px 0 40px;
  border-top: 1px solid #f1f5f9;
}

.footer-content {
  max-width: 1280px;
  margin: 0 auto;
  padding: 0 24px;
}

.footer-brand {
  .footer-logo {
    font-size: 24px;
    font-weight: 800;
    color: @text-main;
    margin-bottom: 16px;
  }
  
  p {
    color: @text-light;
    line-height: 1.6;
    margin-bottom: 24px;
    max-width: 300px;
  }
  
  .social-links {
    display: flex;
    gap: 16px;
    
    a {
      width: 36px;
      height: 36px;
      background: #f1f5f9;
      border-radius: 50%;
      display: flex;
      align-items: center;
      justify-content: center;
      color: @text-main;
      transition: all 0.3s;
      
      &:hover {
        background: @primary-color;
        color: #fff;
      }
    }
  }
}

.footer h4 {
  font-size: 16px;
  font-weight: 700;
  color: @text-main;
  margin-bottom: 24px;
}

.footer ul {
  list-style: none;
  padding: 0;
  margin: 0;
  
  li {
    margin-bottom: 12px;
    color: @text-light;
    font-size: 14px;
    
    a {
      color: @text-light;
      &:hover { color: @primary-color; }
    }
  }
}

.copyright {
  border-top: 1px solid #f1f5f9;
  margin-top: 60px;
  padding-top: 30px;
  text-align: center;
  color: #94a3b8;
  font-size: 14px;
}

/* Back Top */
.back-to-top-btn {
  width: 48px;
  height: 48px;
  background: @primary-color;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #fff;
  font-size: 24px;
  box-shadow: 0 8px 20px rgba(61, 90, 254, 0.4);
  transition: all 0.3s;
  
  &:hover {
    transform: translateY(-5px);
    box-shadow: 0 12px 24px rgba(61, 90, 254, 0.5);
    background: lighten(@primary-color, 5%);
  }
}

// Responsive
@media (max-width: 768px) {
  .header {
    height: 60px;
    line-height: 60px;
    padding: 0 12px;
  }
  .header-content {
    padding: 0;
  }
  .logo-text {
    font-size: 18px;
  }
  .nav-menu, .auth-buttons .login-btn, .auth-buttons .register-btn {
    display: none;
  }
  .mobile-menu-btn {
    display: block;
  }

  /* Mobile Menu Styles */
  /deep/ .mobile-menu-drawer .ant-drawer-body {
    padding: 0;
    height: 100%;
  }

  .drawer-content {
    display: flex;
    flex-direction: column;
    height: 100%;
    padding: 24px;
    background: #fff;
  }

  .drawer-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 40px;
    
    .drawer-brand {
      display: flex;
      align-items: center;
      gap: 12px;
      
      .logo-icon {
        width: 32px;
        height: 32px;
        background: linear-gradient(135deg, @primary-color, @secondary-color);
        border-radius: 8px;
        color: #fff;
        display: flex;
        align-items: center;
        justify-content: center;
        font-weight: 800;
        font-size: 18px;
      }
      
      .logo-text {
        font-size: 20px;
        font-weight: 800;
        color: @text-main;
      }
    }
    
    .close-btn {
      font-size: 20px;
      color: @text-light;
      cursor: pointer;
      padding: 8px;
      border-radius: 50%;
      background: #f1f5f9;
      width: 36px;
      height: 36px;
      display: flex;
      align-items: center;
      justify-content: center;
      transition: all 0.3s;
      
      &:active {
        background: #e2e8f0;
        transform: scale(0.95);
      }
    }
  }

  .drawer-menu {
    flex: 1;
    display: flex;
    flex-direction: column;
    gap: 12px;
    
    .menu-link {
      display: flex;
      align-items: center;
      gap: 12px;
      padding: 16px;
      border-radius: 12px;
      color: @text-main;
      font-size: 16px;
      font-weight: 600;
      transition: all 0.3s;
      background: #f8fafc;
      
      i {
        font-size: 18px;
        color: @text-light;
        transition: color 0.3s;
      }
      
      &.active, &:active {
        background: #eff6ff;
        color: @primary-color;
        
        i {
          color: @primary-color;
        }
      }
    }
  }

  .drawer-auth {
    display: flex;
    flex-direction: column;
    gap: 16px;
    margin-top: 24px;
    padding-top: 24px;
    border-top: 1px solid #f1f5f9;
    
    .drawer-user-info {
      display: flex;
      align-items: center;
      gap: 16px;
      padding: 12px;
      background: #f8fafc;
      border-radius: 12px;
      margin-bottom: 8px;
      
      .avatar {
        background: linear-gradient(135deg, @primary-color, @secondary-color);
        box-shadow: 0 4px 12px rgba(24, 144, 255, 0.2);
      }
      
      .user-details {
        display: flex;
        flex-direction: column;
        
        .username {
          font-weight: 700;
          color: @text-main;
          font-size: 16px;
        }
        
        .status {
          font-size: 12px;
          color: #10b981;
          display: flex;
          align-items: center;
          gap: 4px;
          
          &::before {
            content: '';
            width: 6px;
            height: 6px;
            background: #10b981;
            border-radius: 50%;
            display: inline-block;
          }
        }
      }
    }
    
    .drawer-login-btn, .drawer-dashboard-btn {
      border-radius: 12px;
      height: 48px;
      font-weight: 600;
      font-size: 16px;
      border: 1px solid #e2e8f0;
      color: @text-main;
      display: flex;
      align-items: center;
      justify-content: center;
      gap: 8px;
      
      &:active {
        background: #f1f5f9;
      }
    }
    
    .drawer-logout-btn {
      border-radius: 12px;
      height: 48px;
      font-weight: 600;
      font-size: 16px;
      display: flex;
      align-items: center;
      justify-content: center;
      gap: 8px;
    }
    
    .drawer-register-btn {
      border-radius: 12px;
      height: 48px;
      font-weight: 600;
      font-size: 16px;
      background: @text-main;
      border-color: @text-main;
      box-shadow: 0 4px 12px rgba(0,0,0,0.1);
      
      &:active {
        background: @primary-color;
        border-color: @primary-color;
        transform: scale(0.98);
      }
    }
  }

  .hero-section {
    padding: 100px 0 60px;
  }
  .hero-title {
    font-size: 32px !important;
    padding: 0 16px;
  }
  .hero-subtitle {
    font-size: 16px;
    padding: 0 20px;
    margin-bottom: 30px;
  }
  .hero-actions {
    flex-direction: column;
    padding: 0 24px;
    align-items: center;
    .cta-btn, .contact-btn {
      width: 100%;
      margin: 0 0 16px 0;
    }
  }

  .banner-features-grid {
    grid-template-columns: 1fr;
    padding: 0 24px;
  }

  .stats-container {
    flex-direction: column;
    gap: 24px;
  }

  .features-section {
    padding: 60px 0;
  }
  .features-grid, .use-cases-grid {
    grid-template-columns: 1fr;
    padding: 0 24px;
  }

  .product-section {
    padding: 60px 0;
  }
  .product-container .ant-row-flex {
    flex-direction: column-reverse;
  }
  .product-info {
    padding-right: 0;
    margin-top: 40px;
    text-align: center;
  }
  .product-specs {
    justify-content: center;
    flex-wrap: wrap;
    gap: 20px;
  }
  .product-visual {
    margin-bottom: 20px;
  }
  
  .global-map-section {
    padding: 60px 0;
  }

  .connect-section {
    display: none;
  }

  .footer {
    padding: 40px 0 20px;
    text-align: center;
  }
  .footer-brand {
    margin-bottom: 40px;
    .social-links {
      justify-content: center;
    }
    p {
      margin-left: auto;
      margin-right: auto;
    }
  }
  .footer .ant-col-8, .footer .ant-col-16 {
    width: 100% !important;
    flex: 0 0 100% !important;
    max-width: 100% !important;
    margin-bottom: 30px;
  }
  .footer .ant-col-span-8 {
    width: 100% !important;
    flex: 0 0 100% !important;
    max-width: 100% !important;
    display: block;
    margin-bottom: 20px;
  }
}
</style>
